import { Button, Chip, Input, Select } from '@sjuni/ui';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { MediaPicker } from '@/components/media/MediaPicker';
import * as auth from '@/lib/auth/api';
import { useAuth } from '@/lib/auth/useAuth';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { MediaRow } from '@/routes/media/types';

import { ADMIN_LOCALES } from './types';

interface ProfileFields {
  name: string;
  locale: string;
  avatar_media_id: number | null;
}

/**
 * docs/05-ADMIN-DASHBOARD.md's route table — `/profile`, "own profile & 2FA",
 * open to anyone signed in.
 *
 * The three things a person may change about themselves: their name, the
 * language the dashboard speaks to them in (§6.6), and their picture. Role,
 * faculty and active status are somebody else's decision, edited from `/users`
 * by an administrator who has the permission — `ProfileRequest` refuses them
 * here, and the note in that class says why at length.
 *
 * The password and the second factor keep their own screens. This links to
 * them rather than growing a second copy of two flows that already work.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [avatar, setAvatar] = useState<MediaRow | null>(user?.avatar ?? null);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFields>({
    defaultValues: {
      name: user?.name ?? '',
      locale: user?.locale ?? 'ka',
      avatar_media_id: user?.avatar_media_id ?? null,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setSaved(false);

    try {
      const response = await auth.updateProfile(values);

      /*
       * The header draws its name and picture from the session, so it has to
       * be told: without this an editor saves a new photograph and goes on
       * looking at the old one, which reads as a save that did not work.
       */
      updateUser(response.data);
      setAvatar(response.data.avatar);
      setSaved(true);
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'name',
        'locale',
        'avatar_media_id',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('profile.title')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{user.email}</p>

        {/*
          Role and faculty, shown and not editable — the two facts that decide
          what this account can do, in the one place its owner looks for them.
        */}
        <p className="mt-3 flex flex-wrap gap-2">
          {user.roles.map((role) => (
            <Chip key={role} tone="navy">
              {t(`roles.${role}`, role)}
            </Chip>
          ))}

          {user.faculty?.name ? <Chip tone="gold">{user.faculty.name}</Chip> : null}
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={formError} />

        <MediaPicker
          label={t('profile.fields.avatar')}
          hint={t('profile.fields.avatarHint')}
          value={avatar}
          onChange={(mediaId, media) => {
            setValue('avatar_media_id', mediaId, { shouldDirty: true });
            setAvatar(media);
            setSaved(false);
          }}
          error={errors.avatar_media_id?.message}
        />

        <Input
          {...register('name', { required: t('profile.validation.nameRequired') })}
          label={t('profile.fields.name')}
          autoComplete="name"
          error={errors.name?.message}
          required
        />

        {/* §6.6 — the language of the dashboard, not of the content. */}
        <Select
          {...register('locale')}
          label={t('profile.fields.locale')}
          hint={t('profile.fields.localeHint')}
          value={watch('locale')}
          error={errors.locale?.message}
          options={ADMIN_LOCALES.map((code) => ({
            value: code,
            label: t(`users.locales.${code}`),
          }))}
        />

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>

          {/* Polite: a save is not an error, and it must not steal focus. */}
          <span aria-live="polite" className="text-body-sm text-secondary">
            {saved ? t('profile.saved') : ''}
          </span>
        </div>
      </form>

      <div className="space-y-3 rounded-card border border-subtle p-4">
        <h2 className="text-h4 font-extrabold text-primary">{t('profile.security')}</h2>

        <p className="text-body-sm text-secondary">
          {user.two_factor.confirmed
            ? t('profile.twoFactorOn')
            : user.two_factor.required
              ? t('profile.twoFactorRequired')
              : t('profile.twoFactorOff')}
        </p>

        <div className="flex flex-wrap gap-2">
          {/*
            Buttons that navigate rather than `href` anchors: `Button`'s `href`
            renders a plain `<a>`, which would reload the whole SPA to reach a
            route it already has.
          */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            iconLeft={<KeyRound size={14} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => navigate('/forgot-password')}
          >
            {t('profile.changePassword')}
          </Button>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            iconLeft={<ShieldCheck size={14} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => navigate('/login/setup')}
          >
            {user.two_factor.confirmed ? t('profile.manageTwoFactor') : t('profile.setUpTwoFactor')}
          </Button>
        </div>
      </div>
    </div>
  );
}
